import type { CqHop, CqHopState, CqTraceDetail } from '../api/connectionQuality'

/**
 * Graficul traseului, in stilul ThousandEyes: nodurile de la stanga la
 * dreapta - aparatul, routerul (TTL 1), hopurile, tinta - legate prin linii.
 *
 * Culoarea spune starea hopului, calculata pe server (TracePath::analyze):
 *  - verde  = raspunde fara pierderi care sa continue;
 *  - rosu   = pierdere / intarziere care continua pana la tinta (problema);
 *  - gri deschis cu contur = pierde doar el (routerul isi limiteaza
 *    raspunsurile la ping) - NU e o problema, si nu se coloreaza ca una;
 *  - gri cu "*" = n-a raspuns deloc.
 * Starea se scrie si in text sub nod, ca sa nu fie citita numai din culoare.
 *
 * SVG simplu, fara biblioteca, ca CqChart. Latime fixa pe nod; pe un traseu
 * lung se deruleaza orizontal in cutia lui.
 */

const STEP = 124
const PAD = 60
const Y = 40
const R = 15

const FILL: Record<CqHopState | 'device' | 'unreached', string> = {
  ok: '#1b7a42',
  bad: '#c0392b',
  limited: '#eef1f4',
  silent: '#cfd6dd',
  device: '#2f7ed8',
  unreached: '#ffffff',
}

const STATE_TEXT: Record<CqHopState, string> = {
  ok: 'ok',
  bad: 'problem',
  limited: 'rate-limited, not a problem',
  silent: 'no answer',
}

type Node = {
  key: string
  name: string
  address: string
  metrics: string
  state: CqHopState | 'device' | 'unreached'
  origin: boolean
  title: string
}

function short(s: string, n = 18): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s
}

function hopNode(hop: CqHop, index: number, last: boolean, reached: boolean): Node {
  const name = hop.ttl === 1 ? 'Router' : last && reached ? 'Target' : `Hop ${hop.ttl}`
  const address = hop.ip === null ? '*' : (hop.host ?? hop.ip)
  const metrics =
    hop.state === 'silent'
      ? STATE_TEXT.silent
      : `${hop.loss_pct ?? 0}% loss · ${hop.rtt_avg === null ? '-' : `${hop.rtt_avg} ms`}`
  return {
    key: `hop-${hop.ttl}-${index}`,
    name,
    address,
    metrics,
    state: hop.state,
    origin: hop.origin,
    title: [`${name} (TTL ${hop.ttl})`, hop.ip ?? 'no answer', hop.host ?? '', STATE_TEXT[hop.state], hop.note].filter(Boolean).join(' - '),
  }
}

export function CqPathGraph({ trace, deviceName = 'This device' }: { trace: CqTraceDetail; deviceName?: string }) {
  const nodes: Node[] = [
    { key: 'device', name: deviceName, address: '', metrics: '', state: 'device', origin: false, title: deviceName },
    ...trace.hops.map((h, i) => hopNode(h, i, i === trace.hops.length - 1, trace.reached)),
  ]
  if (!trace.reached) {
    nodes.push({
      key: 'target',
      name: 'Target',
      address: trace.target ?? '',
      metrics: 'not reached',
      state: 'unreached',
      origin: false,
      title: `Target ${trace.target ?? ''} - not reached`,
    })
  }
  const width = PAD * 2 + STEP * (nodes.length - 1)
  const height = 118
  const cx = (i: number) => PAD + i * STEP

  return (
    <div className="cq-path-graph">
      <svg width={width} height={height} role="img" aria-label={`Path to ${trace.target ?? 'the target'}, ${trace.hops.length} hops`}>
        {nodes.slice(1).map((n, i) => {
          const bad = n.state === 'bad'
          const faint = n.state === 'silent' || n.state === 'unreached'
          return (
            <line
              key={`edge-${n.key}`}
              x1={cx(i) + R}
              x2={cx(i + 1) - R}
              y1={Y}
              y2={Y}
              stroke={bad ? '#c0392b' : '#aab4bf'}
              strokeWidth={bad ? 3 : 2}
              strokeDasharray={faint ? '4 4' : undefined}
            />
          )
        })}
        {nodes.map((n, i) => (
          <g key={n.key} data-hop-state={n.state} data-origin={n.origin ? 'yes' : undefined}>
            <title>{n.title}</title>
            {n.origin ? <circle cx={cx(i)} cy={Y} r={R + 5} fill="none" stroke="#c0392b" strokeWidth={2} /> : null}
            <circle
              cx={cx(i)}
              cy={Y}
              r={R}
              fill={FILL[n.state]}
              stroke={n.state === 'limited' || n.state === 'unreached' ? '#8a97a5' : '#ffffff'}
              strokeWidth={2}
              strokeDasharray={n.state === 'unreached' ? '3 3' : undefined}
            />
            {n.state === 'silent' ? (
              <text x={cx(i)} y={Y + 5} textAnchor="middle" className="cq-path-star">
                *
              </text>
            ) : null}
            <text x={cx(i)} y={Y + R + 16} textAnchor="middle" className="cq-path-name">
              {short(n.name, 20)}
            </text>
            {n.address ? (
              <text x={cx(i)} y={Y + R + 30} textAnchor="middle" className="cq-path-addr">
                {short(n.address)}
              </text>
            ) : null}
            {n.metrics ? (
              <text x={cx(i)} y={Y + R + 44} textAnchor="middle" className={`cq-path-metrics${n.state === 'bad' ? ' is-bad' : ''}`}>
                {n.metrics}
              </text>
            ) : null}
          </g>
        ))}
      </svg>
    </div>
  )
}
